import React, { useEffect, useState } from 'react';
import { logError } from '@/lib/error-handler';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Mail,
  Phone,
  Calendar,
  FileText,
  CheckSquare,
  File,
  Plus,
  Clock,
  User,
  TrendingUp,
} from 'lucide-react';
import {
  contactScoringService,
  ContactActivity,
  ContactScore,
} from '@/services/analytics/contactScoringService';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface ContactActivityFeedProps {
  contactId: string;
  showScore?: boolean;
  limit?: number;
}

export function ContactActivityFeed({
  contactId,
  showScore = true,
  limit = 20,
}: ContactActivityFeedProps) {
  const [activities, setActivities] = useState<ContactActivity[]>([]);
  const [contactScore, setContactScore] = useState<ContactScore | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [contactId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [activitiesData, scoreData] = await Promise.all([
        contactScoringService.getContactActivityFeed(contactId, limit),
        showScore ? contactScoringService.getContactScore(contactId) : Promise.resolve(null),
      ]);

      setActivities(activitiesData);
      setContactScore(scoreData);
    } catch (error) {
      logError('Error loading contact data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'call':
        return <Phone className="w-4 h-4" />;
      case 'meeting':
        return <Calendar className="w-4 h-4" />;
      case 'note':
        return <FileText className="w-4 h-4" />;
      case 'task':
        return <CheckSquare className="w-4 h-4" />;
      case 'document':
        return <File className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'email':
        return 'text-blue-600 bg-blue-50';
      case 'call':
        return 'text-green-600 bg-green-50';
      case 'meeting':
        return 'text-purple-600 bg-purple-50';
      case 'note':
        return 'text-gray-600 bg-gray-50';
      case 'task':
        return 'text-orange-600 bg-orange-50';
      case 'document':
        return 'text-indigo-600 bg-indigo-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'cancelled':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getScoreColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Activity Feed</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Activity Feed
          </CardTitle>
          <Button variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-1" />
            Add Activity
          </Button>
        </div>

        {showScore && contactScore && (
          <div className="flex items-center gap-4 pt-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Contact Score:</span>
              <Badge className={getScoreColor(contactScore.score_level)}>
                {contactScore.overall_score}/100 ({contactScore.score_level})
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              Last updated: {formatDistanceToNow(new Date(contactScore.last_calculated))} ago
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0">
        {activities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No activities recorded yet.</p>
            <Button variant="outline" size="sm" className="mt-2">
              <Plus className="w-4 h-4 mr-1" />
              Log First Activity
            </Button>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-4">
              {activities.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg border">
                  <div className={cn('p-2 rounded-full', getActivityColor(activity.activity_type))}>
                    {getActivityIcon(activity.activity_type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-sm truncate">{activity.title}</h4>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {activity.direction && (
                          <Badge variant="outline" className="text-xs">
                            {activity.direction}
                          </Badge>
                        )}
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(new Date(activity.created_at))} ago
                      </div>
                    </div>

                    {activity.description && (
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                        {activity.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between">
                      <Badge className={getStatusColor(activity.status)}>{activity.status}</Badge>

                      {activity.due_date && (
                        <div className="text-xs text-muted-foreground">
                          Due: {new Date(activity.due_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
